import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, Button, Row, Col } from 'react-bootstrap';
import Swal from 'sweetalert2';
import Navigation from './Navigation';
import useAuth from '../../hooks/useAuth';
import { FaBullseye } from 'react-icons/fa';
import AdminFormLayout from './AdminFormLayout';

function AddMission() {
  const [form, setForm] = useState({
    title: '',
    description: '',
    type: 'daily',
    point: '',
    duration_days: '',
    is_active: true,
    repeatable: false,
    url_check: '',
    image_url: ''
  });
  const [imageFile, setImageFile] = useState(null);
  const [previewImage, setPreviewImage] = useState(null);
  const [outlets, setOutlets] = useState([]);
  const [selectedOutlet, setSelectedOutlet] = useState('');
  const { verifyAndRefreshToken } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOutlets = async () => {
      const token = localStorage.getItem('sessionToken');
      try {
        const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}outletlist`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ limit: 100, start: 0 }),
        });
        const json = await response.json();
        if (Array.isArray(json.results)) {
            setOutlets(json.results);
            if (json.results.length > 0) setSelectedOutlet(json.results[0].id);
        }

      } catch (err) {
        console.error('Failed to load outlets:', err);
      }
    };
    fetchOutlets();
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const val =
      type === 'checkbox'
        ? checked
        : type === 'number'
          ? Number(value)
          : value;

    setForm(prev => ({ ...prev, [name]: val }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  const handleUploadImage = async () => {
    const token = localStorage.getItem('sessionToken');
    const formData = new FormData();
    formData.append('image', imageFile);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/upload-image`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData
      });
      const json = await res.json();
      if (json.error === "0") {
        return json.filename;
      } else {
        Swal.fire('Error', json.message, 'error');
      }
    } catch (err) {
      console.log('Upload image error:', err);
      Swal.fire('Error', err.message, 'error');
      return null;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const token = localStorage.getItem('sessionToken');
    const isValid = await verifyAndRefreshToken(token);
    if (!isValid) return navigate('/loginadmin');

    let filename = form.image_url;
    if (imageFile) {
      filename = await handleUploadImage();
      if (!filename) return;
    }

    const missionData = {
      ...form,
      point: Number(form.point),
      duration_days: Number(form.duration_days),
      image_url: filename,
      outlet_id: Number(selectedOutlet),
    };

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}admin/insert-mission`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(missionData),
      });

      const json = await res.json();
      if (json.error === "0") {
        Swal.fire('Success', 'Mission added successfully!', 'success');
        navigate('/missions');
      } else {
        Swal.fire('Error', json.message || 'Failed to add mission', 'error');
      }
    } catch (err) {
      console.error('Add mission error:', err);
      Swal.fire('Error', 'Failed to add mission', 'error');
    }
  };

  return (
    <div>
      <Navigation />
      <AdminFormLayout
        icon={FaBullseye}
        eyebrow="Engagement program"
        title="Create Mission"
        description="Tambahkan misi baru untuk mendukung program loyalitas member dengan aturan yang jelas dan terukur."
        breadcrumbs={[
          { label: 'Dashboard', to: '/dashboardadmin' },
          { label: 'Mission Administration', to: '/missions' },
          { label: 'Create Mission' },
        ]}
        tipsTitle="Mission Notes"
        tips={[
          'Gunakan judul dan deskripsi yang jelas agar member memahami tujuan misi.',
          'Pastikan poin, durasi, dan outlet telah ditentukan dengan benar.',
          'Unggah gambar pendukung apabila diperlukan untuk memperjelas informasi misi.',
        ]}
      >
        <Form onSubmit={handleSubmit}>
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Title</Form.Label>
                <Form.Control
                  type="text"
                  name="title"
                  value={form.title}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  name="description"
                  value={form.description}
                  onChange={handleChange}
                  rows={3}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Type</Form.Label>
                <Form.Select name="type" value={form.type} onChange={handleChange}>
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="special">Special</option>
                </Form.Select>
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Point</Form.Label>
                <Form.Control
                  type="number"
                  name="point"
                  value={form.point}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Duration Days</Form.Label>
                <Form.Control
                  type="number"
                  name="duration_days"
                  value={form.duration_days}
                  onChange={handleChange}
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Outlet</Form.Label>
                <Form.Select
                  value={selectedOutlet}
                  onChange={(e) => setSelectedOutlet(e.target.value)}
                  required
                >
                  {outlets.map((outlet) => (
                    <option key={outlet.id} value={outlet.id}>
                    {outlet.outlet_name}
                    </option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Url Check</Form.Label>
                <Form.Control
                  type="text"
                  name="url_check"
                  value={form.url_check}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Is Active"
                  name="is_active"
                  checked={form.is_active}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  label="Repeatable"
                  name="repeatable"
                  checked={form.repeatable}
                  onChange={handleChange}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Upload Image</Form.Label>
                <Form.Control type="file" accept="image/*" onChange={handleImageChange} />
              </Form.Group>

              {previewImage && (
                <div className="mb-3">
                  <img
                    src={previewImage}
                    alt="Preview"
                    style={{ width: '100%', maxHeight: '200px', objectFit: 'cover' }}
                  />
                </div>
              )}
            </Col>
          </Row>
          <div className="form-actions">
            <Button type="submit" variant="primary">Save Mission</Button>
            <Button variant="secondary" onClick={() => navigate('/missions')}>Cancel</Button>
          </div>
        </Form>
      </AdminFormLayout>
    </div>
  );
}

export default AddMission;
